import React, { useEffect } from "react";
import Header from "./Header";
import Footer from "./Footer";
import WhatsAppWidget from "./WhatsAppWidget";
import { Cloud, ShieldCheck, Database, Cpu, ArrowRight, Zap, Monitor, Globe, Server } from "lucide-react";
import { Link } from "react-router-dom";

const ICTPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      title: "Cyber Security",
      description: "Proteggi la tua infrastruttura aziendale con soluzioni avanzate di sicurezza perimetrale, protezione endpoint e monitoraggio H24.",
      icon: <ShieldCheck className="w-12 h-12 text-orange-500" />,
      features: ["SOC Monitoraggio 24/7", "Firewall di nuova generazione", "Vulnerability Assessment", "Cloud Security"]
    },
    {
      title: "Cloud & Data Center",
      description: "Soluzioni cloud scalabili e sicure per ospitare i tuoi applicativi, database e backup, garantendo continuità operativa e flessibilità.",
      icon: <Cloud className="w-12 h-12 text-orange-500" />,
      features: ["Public & Private Cloud", "Backup & Disaster Recovery", "IaaS & PaaS", "Virtual Desktop Infrastructure"]
    },
    {
      title: "Smart Working & Unified Comm.",
      description: "Strumenti collaborativi integrati per connettere i tuoi dipendenti ovunque si trovino, migliorando la produttività e la comunicazione.",
      icon: <Monitor className="w-12 h-12 text-orange-500" />,
      features: ["Microsoft 365 Business", "Webinar & Video Conf.", "Centralini Cloud Integrati", "Digital Collaboration"]
    },
    {
      title: "Internet of Things (IoT)",
      description: "Connetti i tuoi asset e ottimizza i processi aziendali attraverso la raccolta e l'analisi dei dati in tempo reale.",
      icon: <Cpu className="w-12 h-12 text-orange-500" />,
      features: ["Monitoraggio Flotte", "Smart Metering", "Asset Tracking", "M2M Connectivity"]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Futuristic Hero Section */}
      <section className="relative pt-40 pb-32 overflow-hidden bg-gray-900 text-white">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-orange-500/10 to-transparent" />
          <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-black to-transparent" />
        </div>
        
        <div className="container relative z-10 mx-auto px-4">
          <div className="max-w-4xl">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-orange-400 mb-8">
              <Zap className="w-4 h-4" />
              <span className="text-xs font-black uppercase tracking-widest">Digital Transformation 2026</span>
            </div>
            <h1 className="text-6xl md:text-8xl font-black mb-8 tracking-tighter leading-none italic">
              L'Innovazione che <span className="text-orange-500 not-italic">accelera</span> il tuo Business.
            </h1>
            <p className="text-xl md:text-2xl text-gray-400 max-w-2xl leading-relaxed mb-12">
              Soluzioni ICT e Cloud su misura progettate per garantire sicurezza, efficienza e competitività alla tua impresa.
            </p>
            <div className="flex flex-wrap gap-6">
              <Link to="/preventivo" className="bg-orange-500 text-white px-10 py-5 rounded-2xl font-black text-lg hover:bg-white hover:text-black transition-all duration-300">
                Richiedi Consulenza ICT
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-32 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-24">
            <h2 className="text-4xl md:text-6xl font-black text-gray-900 tracking-tighter mb-6 uppercase">Il Nostro Ecosystem Digitale</h2>
            <div className="w-24 h-2 bg-orange-500 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <div key={index} className="bg-white p-12 rounded-[3rem] shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100 group">
                <div className="mb-8 p-4 bg-orange-50 rounded-3xl inline-block group-hover:bg-orange-500 group-hover:text-white transition-colors duration-500">
                  {React.cloneElement(service.icon, { className: "w-12 h-12 group-hover:text-white transition-colors" })}
                </div>
                <h3 className="text-3xl font-black text-gray-900 mb-6 tracking-tight uppercase">{service.title}</h3>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  {service.description}
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center space-x-2 text-sm font-bold text-gray-500 uppercase tracking-tight">
                      <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Lovecchio ICT */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-4">
          <div className="bg-gray-900 rounded-[4rem] p-12 md:p-24 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/20 rounded-full blur-[120px] -mr-48 -mt-48" />
            <div className="relative z-10 grid md:grid-cols-2 gap-20 items-center">
              <div>
                <h2 className="text-5xl md:text-6xl font-black text-white mb-8 tracking-tighter leading-tight">
                  Sicurezza e <span className="text-orange-500">Affidabilità</span> certificate.
                </h2>
                <p className="text-xl text-gray-400 mb-12 leading-relaxed">
                  Siamo partner certificati WindTre Business e leader nella fornitura di servizi ICT per le aziende. La nostra missione è semplificare la complessità tecnologica.
                </p>
                <div className="grid grid-cols-2 gap-8">
                  <div className="flex flex-col">
                    <span className="text-4xl font-black text-white mb-1 tracking-tighter">99.9%</span>
                    <span className="text-sm font-bold text-orange-500 uppercase tracking-widest">Uptime Garantito</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-4xl font-black text-white mb-1 tracking-tighter">24/7</span>
                    <span className="text-sm font-bold text-orange-500 uppercase tracking-widest">Supporto Tecnico</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="aspect-square bg-white/5 border border-white/10 rounded-full p-12 flex items-center justify-center animate-pulse">
                  <Server className="w-48 h-48 text-orange-500 opacity-50" />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                   <Globe className="w-32 h-32 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl md:text-7xl font-black text-gray-900 mb-8 tracking-tighter uppercase">Pronto per il salto tecnologico?</h2>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">Contatta oggi i nostri esperti ICT per un'analisi gratuita della tua infrastruttura aziendale.</p>
          <Link 
            to="/preventivo"
            className="inline-flex items-center justify-center bg-gray-900 text-white px-12 py-6 rounded-2xl font-black text-xl hover:bg-orange-500 transition-all duration-300 group"
          >
            Inizia il Progetto
            <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-2 transition-transform" />
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppWidget />
    </div>
  );
};

export default ICTPage;
